import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Heart,
  Wand2,
  TrendingUp,
  Sparkles,
  Share2,
  Loader2,
  Info,
  Plus,
  AtSign,
  ExternalLink,
  Check,
  X,
  Mail,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import SiteFooter from '../components/SiteFooter';
import AuthDialog from '../components/AuthDialog';
import { Textarea } from '../components/ui/textarea';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GRADIENT_MAP = {
  'cyan-magenta': 'linear-gradient(135deg, #00F0FF 0%, #FF007F 100%)',
  'silver-slate': 'linear-gradient(135deg, #e2e8f0 0%, #64748b 100%)',
  'emerald-lime': 'linear-gradient(135deg, #10b981 0%, #a3e635 100%)',
  'amber-crimson': 'linear-gradient(135deg, #f59e0b 0%, #dc2626 100%)',
  'sky-lavender': 'linear-gradient(135deg, #38bdf8 0%, #c084fc 100%)',
  'violet-gold': 'linear-gradient(135deg, #8b5cf6 0%, #fbbf24 100%)',
};
const gradientFor = (k) => GRADIENT_MAP[k] || GRADIENT_MAP['cyan-magenta'];

export default function MySoulsPage() {
  const [souls, setSouls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorKey, setAuthorKey] = useState(null);
  const [author, setAuthor] = useState(null); // { handle, name, bio, email, link }
  const [authOpen, setAuthOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = async (key) => {
    if (!key) return;
    try {
      const [rSouls, rAuthor] = await Promise.all([
        fetch(`${API}/souls/mine?author_key=${encodeURIComponent(key)}`),
        fetch(`${API}/me/author?author_key=${encodeURIComponent(key)}`, { credentials: 'include' }),
      ]);
      if (rSouls.ok) setSouls(await rSouls.json());
      if (rAuthor.ok) setAuthor(await rAuthor.json());
    } catch { /* noop */ }
  };

  useEffect(() => {
    let key = localStorage.getItem('atw_author_key');
    setAuthorKey(key);
    (async () => {
      // If we don't have a local author_key but the user is signed in, ask the server for
      // their linked profile — this makes /souls/mine work on a fresh device.
      if (!key) {
        try {
          const r = await fetch(`${API}/me/author`, { credentials: 'include' });
          if (r.ok) {
            const doc = await r.json();
            if (doc?.author_key) {
              localStorage.setItem('atw_author_key', doc.author_key);
              key = doc.author_key;
              setAuthorKey(key);
              setAuthor(doc);
            }
          }
        } catch { /* noop */ }
      }
      if (key) await refresh(key);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalLikes = souls.reduce((n, s) => n + (s.likes || 0), 0);
  const totalUses = souls.reduce((n, s) => n + (s.uses || 0), 0);

  const shareSoul = (soul) => {
    const url = `${BACKEND_URL}/api/share/soul/${soul.slug}`;
    try {
      navigator.clipboard.writeText(url);
      toast({ title: 'Share link copied', description: 'Paste it into Twitter/LinkedIn — the preview card shows automatically.' });
    } catch {
      toast({ title: 'Share URL', description: url });
    }
  };

  return (
    <div className="relative bg-[#030303] text-white min-h-screen overflow-hidden">
      <Navbar onGetStarted={() => setAuthOpen(true)} />

      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle, #00F0FF33 0%, transparent 70%)' }}
      />

      <section className="relative pt-32 pb-6 px-6 lg:px-10 max-w-5xl mx-auto">
        <Link
          to="/souls"
          data-testid="mine-back-link"
          className="inline-flex items-center gap-1.5 text-[13px] text-white/60 hover:text-white transition"
        >
          <ArrowLeft size={13} /> Back to SOUL Library
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-6"
        >
          <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">Your souls</div>
          <h1 className="mt-2 font-display font-black tracking-[-0.03em] text-4xl md:text-5xl"
            style={{ background: 'linear-gradient(180deg, #ffffff 0%, #e2e8f0 55%, #94a3b8 100%)',
                     WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Watch your personalities spread.
          </h1>
          <p className="mt-3 text-white/70 max-w-2xl text-[15px] leading-relaxed">
            Every soul you publish is tracked below with live likes and uses. Share the link — every new fan lands in the builder with your soul already loaded.
          </p>
        </motion.div>

        {/* Stats bar */}
        <div className="mt-8 grid grid-cols-3 gap-3">
          <StatCard
            label="Personalities published"
            value={souls.length}
            icon={Sparkles}
            testid="mine-stat-count"
            gradient="cyan-magenta"
          />
          <StatCard
            label="Total likes"
            value={totalLikes}
            icon={Heart}
            testid="mine-stat-likes"
            gradient="amber-crimson"
          />
          <StatCard
            label="Total uses"
            value={totalUses}
            icon={Wand2}
            testid="mine-stat-uses"
            gradient="sky-lavender"
          />
        </div>

        {/* Public creator page + digest opt-in */}
        {authorKey && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="mine-claim-section">
            <div className="h-12 w-12 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/25 flex items-center justify-center text-[#00F0FF] shrink-0">
              <AtSign size={18} />
            </div>
            <div className="flex-1 min-w-0">
              {author?.handle ? (
                <>
                  <div className="text-[13px] text-white/85 flex items-center gap-2 flex-wrap">
                    Your public URL:
                    <Link to={`/@${author.handle}`} className="font-mono text-white hover:text-cyan-200 transition" data-testid="mine-public-url">/@{author.handle}</Link>
                    {author.user_email && (
                      <span
                        data-testid="mine-synced-chip"
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-400/25 bg-emerald-400/10 text-emerald-200 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5"
                      >
                        <Check size={9} /> Synced
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-white/50 mt-1">
                    {author.user_email
                      ? `Signed in as ${author.user_email} — this handle follows you on every device you log in from.`
                      : 'Share this URL — every persona you publish appears here automatically.'}
                    {' '}{author.email
                      ? 'Weekly digest is ON.'
                      : 'Add your email to get a weekly digest of new likes + uses.'}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[14px] font-semibold text-white">Claim your public creator URL</div>
                  <div className="text-[12.5px] text-white/60 mt-0.5">
                    Get a shareable /@handle page listing every persona you publish. Optionally opt-in to a weekly digest email.
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setClaimOpen(true)}
              data-testid="mine-claim-btn"
              className="shrink-0 rounded-full text-black font-semibold text-[13px] px-4 py-2.5 hover:scale-[1.02] transition inline-flex items-center gap-1.5"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              {author?.handle ? 'Edit profile' : 'Claim handle'} <ExternalLink size={12} />
            </button>
          </div>
        )}

        {/* List */}
        <div className="mt-10">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-white/50">
              <Loader2 size={22} className="animate-spin mr-2" /> Loading your personalities…
            </div>
          ) : !authorKey ? (
            <EmptyState
              title="You haven't published a SOUL yet"
              body="Publish one at the SOUL Library — we'll track its likes + uses right here."
              cta={<Link to="/souls" className="inline-flex items-center gap-1.5 rounded-full text-black font-semibold text-[14px] px-5 py-2.5"
                style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}>
                <Plus size={14} /> Publish your first SOUL
              </Link>}
            />
          ) : souls.length === 0 ? (
            <EmptyState
              title="Nothing yet from this browser"
              body="Personas are tied to the browser they're published from. If you published on another device, they'll show up there."
              cta={<Link to="/souls" className="inline-flex items-center gap-1.5 rounded-full text-black font-semibold text-[14px] px-5 py-2.5"
                style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}>
                <Plus size={14} /> Publish another SOUL
              </Link>}
            />
          ) : (
            <div className="space-y-3" data-testid="mine-list">
              {souls.map((s) => (
                <MySoulRow key={s.slug} soul={s} onShare={shareSoul} />
              ))}
            </div>
          )}
        </div>

        {/* Growth loop tip */}
        <div className="mt-10 flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="h-10 w-10 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/25 flex items-center justify-center text-[#00F0FF] shrink-0">
            <TrendingUp size={16} />
          </div>
          <div className="text-[13.5px] text-white/75 leading-relaxed">
            <span className="font-semibold text-white">Tip.</span> Every time someone opens your share link on Twitter or LinkedIn, the preview shows a custom card of your SOUL — no extra work needed. The URL auto-loads the persona into the builder.
          </div>
        </div>
      </section>

      <div className="h-24" />
      <SiteFooter />
      <ClaimHandleModal
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        authorKey={authorKey}
        initial={author}
        onSaved={async (updated) => {
          setClaimOpen(false);
          await refresh(authorKey);
          toast({ title: '🎉 Profile saved', description: `Your creator page is live at /@${updated.handle}` });
        }}
      />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <Toaster />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid, gradient }) {
  return (
    <div
      data-testid={testid}
      className="relative rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-5 overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full blur-2xl opacity-30"
        style={{ background: gradientFor(gradient) }}
      />
      <div className="relative">
        <div className="flex items-center gap-2 text-white/50">
          <Icon size={13} />
          <span className="text-[10.5px] font-mono uppercase tracking-[0.25em]">{label}</span>
        </div>
        <div className="mt-2 text-3xl md:text-4xl font-display font-black text-white">
          {value}
        </div>
      </div>
    </div>
  );
}

function MySoulRow({ soul, onShare }) {
  return (
    <motion.div
      whileHover={{ x: 2 }}
      transition={{ duration: 0.15 }}
      className="flex flex-col md:flex-row md:items-center gap-4 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-4"
      data-testid={`mine-row-${soul.slug}`}
    >
      <div
        className="h-14 w-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
        style={{ background: gradientFor(soul.gradient) }}
      >
        {soul.emoji || soul.name.slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Link to={`/souls/${soul.slug}`} className="text-[15px] font-bold text-white hover:text-cyan-300 transition truncate">
            {soul.name}
          </Link>
          {soul.featured && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-amber-300 border border-amber-300/30 rounded-full px-1.5 py-0.5">
              Featured
            </span>
          )}
        </div>
        <div className="text-[12.5px] text-white/60 truncate">{soul.tagline}</div>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-white/50">
          <span className="font-mono">/souls/{soul.slug}</span>
          <span>· published {new Date(soul.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex items-center gap-4 text-[12.5px] text-white/70">
        <div className="text-right">
          <div className="text-[9.5px] font-mono uppercase tracking-widest text-white/40">Likes</div>
          <div className="text-lg font-bold text-white">{soul.likes}</div>
        </div>
        <div className="text-right">
          <div className="text-[9.5px] font-mono uppercase tracking-widest text-white/40">Uses</div>
          <div className="text-lg font-bold text-white">{soul.uses}</div>
        </div>
      </div>
      <button
        onClick={() => onShare(soul)}
        data-testid={`mine-share-${soul.slug}`}
        className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white text-[13px] font-medium px-4 py-2.5 flex items-center gap-1.5 transition"
      >
        <Share2 size={13} /> Share
      </button>
    </motion.div>
  );
}

function EmptyState({ title, body, cta }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-white/[0.05] border border-white/10 flex items-center justify-center mb-4">
        <Info size={22} className="text-white/50" />
      </div>
      <div className="text-lg font-bold text-white">{title}</div>
      <div className="mt-2 text-white/60 text-[14px] max-w-md mx-auto">{body}</div>
      <div className="mt-6 flex justify-center">{cta}</div>
    </div>
  );
}

function ClaimHandleModal({ open, onClose, authorKey, initial, onSaved }) {
  const [form, setForm] = useState({
    handle: initial?.handle || '',
    name: initial?.name || '',
    bio: initial?.bio || '',
    email: initial?.email || '',
    link: initial?.link || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [handleStatus, setHandleStatus] = useState({ checking: false, ok: null, msg: null });

  useEffect(() => {
    if (open) {
      setForm({
        handle: initial?.handle || '',
        name: initial?.name || '',
        bio: initial?.bio || '',
        email: initial?.email || '',
        link: initial?.link || '',
      });
      setHandleStatus({ checking: false, ok: null, msg: null });
    }
  }, [open, initial]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Live handle availability check (debounced)
  useEffect(() => {
    if (!open) return;
    const raw = (form.handle || '').trim().toLowerCase();
    const slug = raw.replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slug || slug.length < 2) { setHandleStatus({ checking: false, ok: null, msg: null }); return; }
    if (slug === initial?.handle) { setHandleStatus({ checking: false, ok: true, msg: 'Your current handle' }); return; }
    setHandleStatus({ checking: true, ok: null, msg: null });
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/authors/${encodeURIComponent(slug)}`);
        if (r.status === 404) setHandleStatus({ checking: false, ok: true, msg: 'Available' });
        else setHandleStatus({ checking: false, ok: false, msg: 'Already taken' });
      } catch { setHandleStatus({ checking: false, ok: null, msg: null }); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.handle, open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    const handle = (form.handle || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!handle || handle.length < 2) {
      toast({ title: 'Invalid handle', description: 'Use letters/numbers, min 2 characters.' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/authors/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          author_key: authorKey,
          handle,
          name: form.name || undefined,
          bio: form.bio || undefined,
          email: form.email || undefined,
          link: form.link || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      onSaved?.({ handle: d.handle });
    } catch (err) {
      toast({ title: 'Could not save', description: err.message });
    }
    setSubmitting(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0f]/95 backdrop-blur-2xl overflow-hidden shadow-2xl"
        data-testid="mine-claim-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10.5px] font-mono uppercase tracking-[0.3em] text-white/50">Public profile</div>
            <div className="text-[16px] font-black text-white mt-0.5">Claim your creator handle</div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={submit} noValidate className="p-6 space-y-4">
          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-[0.25em] text-white/50">Handle</label>
            <div className="mt-1.5 flex items-center rounded-xl bg-white/[0.04] border border-white/10 focus-within:border-white/25 transition">
              <span className="pl-4 pr-1 text-white/40 font-mono text-[14px]">/@</span>
              <input
                value={form.handle}
                onChange={update('handle')}
                data-testid="claim-handle-input"
                placeholder="your-handle"
                maxLength={32}
                className="flex-1 h-11 bg-transparent text-[14px] text-white placeholder:text-white/30 focus:outline-none pr-4"
              />
              {handleStatus.msg && (
                <span
                  className={`pr-3 text-[11px] font-medium flex items-center gap-1 ${
                    handleStatus.ok === true ? 'text-emerald-300' : handleStatus.ok === false ? 'text-rose-300' : 'text-white/40'
                  }`}
                >
                  {handleStatus.checking ? <Loader2 size={11} className="animate-spin" /> :
                    handleStatus.ok ? <Check size={11} /> : handleStatus.ok === false ? <X size={11} /> : null}
                  {handleStatus.msg}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10.5px] font-mono uppercase tracking-[0.25em] text-white/50">Display name</label>
              <input
                value={form.name}
                onChange={update('name')}
                data-testid="claim-name-input"
                placeholder="Your name"
                maxLength={60}
                className="mt-1.5 w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
              />
            </div>
            <div>
              <label className="text-[10.5px] font-mono uppercase tracking-[0.25em] text-white/50 flex items-center gap-1"><Mail size={10} /> Email (for weekly digest)</label>
              <input
                value={form.email}
                onChange={update('email')}
                data-testid="claim-email-input"
                type="email"
                placeholder="you@example.com"
                maxLength={200}
                className="mt-1.5 w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
              />
            </div>
          </div>
          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-[0.25em] text-white/50">Short bio</label>
            <Textarea
              value={form.bio}
              onChange={update('bio')}
              data-testid="claim-bio-input"
              placeholder="What kind of personas do you build?"
              rows={2}
              maxLength={280}
              className="mt-1.5 min-h-[64px] w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-2.5 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition resize-none"
            />
          </div>
          <div>
            <label className="text-[10.5px] font-mono uppercase tracking-[0.25em] text-white/50">Twitter / site (optional)</label>
            <input
              value={form.link}
              onChange={update('link')}
              data-testid="claim-link-input"
              placeholder="twitter.com/you"
              maxLength={200}
              className="mt-1.5 w-full h-11 rounded-xl bg-white/[0.04] border border-white/10 px-4 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-white/25 transition"
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white text-[14px] font-medium transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || handleStatus.ok === false}
              data-testid="claim-submit-btn"
              className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-xl text-black text-[14px] font-semibold hover:scale-[1.02] transition disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg, #00F0FF 0%, #FF007F 100%)' }}
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Save profile <Check size={14} /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
